<template>
  <div
    class="cf-question rounded-lg border bg-white transition-all"
    :class="
      isActive
        ? 'cf-question--active border-blue-200'
        : expanded
          ? 'border-gray-200'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
    "
    :data-cf-question-key="modelValue._key"
    @focusin="$emit('activate', modelValue._key)"
  >
    <div class="flex items-center gap-2 px-3 py-2">
      <i
        class="cf-question-handle fas fa-grip-vertical cursor-move text-gray-300 hover:text-gray-500"
        data-cy="question-handle"
      ></i>
      <button
        type="button"
        class="text-gray-500 hover:text-gray-700 focus:outline-none"
        :aria-expanded="expanded"
        data-cy="question-expand"
        @click="expanded = !expanded"
      >
        <i
          class="fas fa-chevron-right transition-transform duration-200"
          :class="{ 'rotate-90': expanded }"
        ></i>
      </button>
      <button
        type="button"
        class="flex min-w-0 flex-1 items-center gap-2 text-left focus:outline-none"
        data-cy="question-summary"
        @click="expanded = !expanded"
      >
        <i
          class="fas w-4 shrink-0 text-center"
          :class="[typeMeta.icon, typeMeta.color]"
        ></i>
        <span
          v-if="modelValue.title"
          class="truncate text-sm font-medium text-gray-900"
          >{{ modelValue.title }}</span
        >
        <span v-else class="truncate text-sm italic text-gray-400"
          >Untitled question</span
        >
        <span class="shrink-0 text-xs text-gray-400">{{ typeMeta.label }}</span>
      </button>
      <div class="flex items-center gap-1">
        <button
          type="button"
          :class="CF_ACTION_BTN"
          title="Duplicate question"
          data-cy="question-duplicate"
          @click="$emit('duplicate')"
        >
          <i class="fas fa-clone"></i>
        </button>
        <button
          type="button"
          :class="CF_ACTION_BTN"
          :disabled="index === 0"
          title="Move up"
          data-cy="question-move-up"
          @click="$emit('move-up')"
        >
          <i class="fas fa-arrow-up"></i>
        </button>
        <button
          type="button"
          :class="CF_ACTION_BTN"
          :disabled="index === total - 1"
          title="Move down"
          data-cy="question-move-down"
          @click="$emit('move-down')"
        >
          <i class="fas fa-arrow-down"></i>
        </button>
        <button
          type="button"
          :class="[CF_ACTION_BTN, 'hover:!text-red-600']"
          title="Remove question"
          data-cy="question-remove"
          @click="requestRemove"
        >
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>

    <div class="cf-collapse" :class="{ 'is-open': expanded }">
      <div :inert="!expanded">
        <div class="space-y-4 border-t border-gray-100 px-3 py-3">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <InputField
              :model-value="modelValue.title"
              title="Title"
              dense
              placeholder="Question title"
              :validation="titleTouched && !modelValue.title ? 'error' : 'none'"
              data-cy="question-title"
              @focusout="titleTouched = true"
              @update:model-value="(v) => patch({ title: v })"
            />
            <CustomFormTypePicker
              :model-value="modelValue.question_type"
              label="Type"
              @update:model-value="(v) => changeType(v)"
            />
          </div>

          <InputField
            :model-value="modelValue.description"
            title="Description"
            dense
            placeholder="Shown to the technician and on the PDF"
            data-cy="question-description"
            @update:model-value="(v) => patch({ description: v })"
          />

          <TextArea
            v-if="showOptions"
            :model-value="optionsText"
            message="Options"
            placeholder="One option per line, or comma-separated"
            :bottom-sub-message="optionCountLabel"
            data-cy="question-options"
            @update:model-value="(v) => updateOptions(v)"
          />

          <div v-if="showUnit" class="w-1/3">
            <InputField
              :model-value="modelValue.unit ?? ''"
              title="Unit"
              dense
              placeholder="e.g. PSI, °F"
              data-cy="question-unit"
              @update:model-value="(v) => patch({ unit: v })"
            />
          </div>

          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <SelectInput
              v-if="!photoOnly"
              :model-value="modelValue.photo_requirement"
              label="Photos"
              :options="PHOTO_REQUIREMENTS"
              data-cy="question-photo-requirement"
              @update:model-value="(v) => changePhotoRequirement(v)"
            />
            <InputField
              v-if="showPhotoMin"
              :model-value="String(modelValue.photo_min_count)"
              title="Minimum photos"
              type="number"
              dense
              data-cy="question-photo-min"
              @update:model-value="
                (v) => patch({ photo_min_count: toCount(v) })
              "
            />
          </div>

          <div class="flex items-center gap-6">
            <Toggle
              :checked="modelValue.required"
              label-after="Required"
              data-cy="question-required"
              @update:checked="(v) => patch({ required: v })"
            />
            <Toggle
              :checked="modelValue.customer_visible"
              label-after="Visible to customer"
              data-cy="question-customer-visible"
              @update:checked="(v) => patch({ customer_visible: v })"
            />
          </div>
        </div>
      </div>
    </div>

    <Modal v-model="showDeleteModal" title="Delete question?" size="small">
      <p class="text-sm text-gray-600">
        Delete
        <span class="font-medium">{{
          modelValue.title || "this question"
        }}</span
        >? This can't be undone.
      </p>
      <template #footer>
        <div class="flex justify-end gap-2 px-4 py-3">
          <SeraButton
            text="Cancel"
            type="secondary"
            data-cy="question-delete-cancel"
            @click="showDeleteModal = false"
          />
          <SeraButton
            text="Delete question"
            type="danger"
            data-cy="question-delete-confirm"
            @click="confirmRemove"
          />
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";

import SeraButton from "@/components/Button.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import Modal from "@/components/Seranova/Modal/Modal.vue";
import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";
import TextArea from "@/components/Seranova/TextArea/TextArea.vue";
import Toggle from "@/components/Seranova/Toggle/Toggle.vue";
import {
  type BuilderQuestion,
  CF_ACTION_BTN,
  isPhotoOnly,
  optionsToText,
  parseOptionList,
  PHOTO_REQUIREMENTS,
  photosRequired,
  questionType,
  typeHasOptions,
  typeHasUnit
} from "./customFormFields";
import CustomFormTypePicker from "./CustomFormTypePicker.vue";

const props = withDefaults(
  defineProps<{
    modelValue: BuilderQuestion;
    index?: number;
    total?: number;
    activeQuestionKey?: string | null;
  }>(),
  { index: 0, total: 1, activeQuestionKey: null }
);
const emit = defineEmits<{
  "update:modelValue": [value: BuilderQuestion];
  activate: [key: string];
  toggle: [key: string, expanded: boolean];
  remove: [];
  duplicate: [];
  "move-up": [];
  "move-down": [];
}>();

const expanded = ref(!props.modelValue.title);
const titleTouched = ref(false);
const showDeleteModal = ref(false);
const isActive = computed(
  () => props.activeQuestionKey === props.modelValue._key
);

// Surface expand/collapse so the outline can highlight open questions; report
// closed on unmount so a removed question drops out of the outline.
watch(expanded, (isOpen) => emit("toggle", props.modelValue._key, isOpen), {
  immediate: true
});
onBeforeUnmount(() => emit("toggle", props.modelValue._key, false));
const optionsText = ref(optionsToText(props.modelValue.options));

const typeMeta = computed(() => questionType(props.modelValue.question_type));
const showOptions = computed(() =>
  typeHasOptions(props.modelValue.question_type)
);
const showUnit = computed(() => typeHasUnit(props.modelValue.question_type));
const photoOnly = computed(() => isPhotoOnly(props.modelValue.question_type));
// Photo-only always takes a minimum count; other types only when photos are
// required. The none/optional/required dropdown is hidden for photo-only.
const showPhotoMin = computed(
  () => photosRequired(props.modelValue.photo_requirement) || photoOnly.value
);
const optionCountLabel = computed(() => {
  const n = props.modelValue.options.length;
  return `${n} ${n === 1 ? "option" : "options"}`;
});

function patch(changes: Partial<BuilderQuestion>) {
  emit("update:modelValue", { ...props.modelValue, ...changes });
}

// A titled question is worth a confirm before deleting; an untitled, just-added
// row has nothing to lose, so it deletes straight away (mirrors section delete).
function requestRemove() {
  if (props.modelValue.title.trim()) {
    showDeleteModal.value = true;
    return;
  }
  emit("remove");
}

function confirmRemove() {
  showDeleteModal.value = false;
  emit("remove");
}

// Clearing options/unit when they no longer apply keeps stale config from being
// saved; options are entered as a list, so there's no blank row to seed.
function changeType(newType: string) {
  const changes: Partial<BuilderQuestion> = { question_type: newType };
  if (!typeHasOptions(newType)) {
    changes.options = [];
    optionsText.value = "";
  }
  if (!typeHasUnit(newType)) changes.unit = null;
  // Photos are the answer for photo-only — require them with a minimum of 1.
  if (isPhotoOnly(newType)) {
    changes.photo_requirement = "required_photos";
    if (props.modelValue.photo_min_count < 1) changes.photo_min_count = 1;
  }
  patch(changes);
}

function updateOptions(text: string) {
  optionsText.value = text;
  patch({ options: parseOptionList(text) });
}

// Requiring photos implies at least one, so default the minimum to 1.
function changePhotoRequirement(requirement: string) {
  const changes: Partial<BuilderQuestion> = { photo_requirement: requirement };
  if (
    requirement === "required_photos" &&
    props.modelValue.photo_min_count < 1
  ) {
    changes.photo_min_count = 1;
  }
  patch(changes);
}

function toCount(value: string): number {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;
}
</script>

<style scoped>
/* Animated accordion: the body is always rendered (so its own state persists)
   but clipped to zero height when collapsed. */
.cf-collapse {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 220ms ease;
}
.cf-collapse.is-open {
  grid-template-rows: 1fr;
}
.cf-collapse > div {
  overflow: hidden;
}

/* The single question being edited: a calm left rail + tint so it stands out
   among many open rows — replacing the heavy border+shadow every expanded row
   used to get, which made it impossible to tell which one had focus. */
.cf-question--active {
  background-color: rgb(239 246 255 / 0.6);
  box-shadow: inset 3px 0 0 0 #3b82f6;
}

/* Brief ring pulse when the outline jumps to this question. */
.cf-jump-pulse {
  animation: cf-jump-pulse 1200ms ease;
}
@keyframes cf-jump-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5);
  }
  100% {
    box-shadow: 0 0 0 12px rgba(59, 130, 246, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .cf-collapse,
  .cf-question,
  .cf-jump-pulse {
    transition: none;
    animation: none;
  }
}
</style>
